import { useCallback, useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import {
  cancelJob,
  fetchJobs,
  launchJob,
  type JobsView,
  type RunView,
} from "../../services/http.js";

/** How often the list refreshes while anything is live. */
const POLL_MS = 3000;

/**
 * The catalog pipeline: launch a stage, watch it, stop it — phase 16i.
 *
 * # ⚠️ The button queues; it does not run anything
 *
 * `POST` puts a row on the queue and returns. `sonolume-worker` claims it and
 * writes progress onto the same row, and this screen reads it back. That is the
 * only shape that survives a stage which runs for four hours — and it is why
 * "queued" is the honest word on the button rather than "run".
 *
 * # ⚠️ The order is the pipeline's, not alphabetical
 *
 * Each stage reads what the previous one wrote. Running `resolve` against a
 * catalogue with one source per track gives every track `probable`, which is
 * dealable by nobody (ADR 0002). The server sends them in order and this renders
 * them in the order it was sent.
 *
 * # Polling, not a socket
 *
 * Three seconds while something is live, and not at all when nothing is. A
 * screen somebody opens for a minute a week does not earn a subscription, and
 * the realtime plane is for the game.
 */
export function JobsPanel() {
  const { t } = useTranslation();
  const [view, setView] = useState<JobsView | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(() => {
    return fetchJobs().then(
      (loaded) => {
        setView(loaded);
      },
      () => {
        setMissing(true);
      },
    );
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const live = view?.runs.some(
    (run) => run.status === "pending" || run.status === "running",
  );

  useEffect(() => {
    if (!live) {
      return;
    }
    const timer = setInterval(() => void load(), POLL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [live, load]);

  if (missing) {
    return <p className="text-sm text-white/50">{t("admin.nothingHere")}</p>;
  }
  if (view === null) {
    return <p className="text-sm text-white/50">{t("jobs.reading")}</p>;
  }

  const start = (kind: string) => {
    setBusy(kind);
    setNote(null);
    void launchJob(kind)
      .then((reply) => {
        setNote(reply.queued ? t("jobs.queued") : t("jobs.alreadyQueued"));
        return load();
      })
      .finally(() => {
        setBusy(null);
      });
  };

  const stop = (id: string) => {
    setBusy(id);
    void cancelJob(id)
      .then(() => {
        setNote(t("jobs.stopping"));
        return load();
      })
      .finally(() => {
        setBusy(null);
      });
  };

  return (
    <section aria-label={t("jobs.title")} className="flex flex-col gap-5">
      <div>
        <h2 className="text-sm font-semibold">{t("jobs.launch")}</h2>
        <p className="mt-1 mb-3 text-xs text-white/50">{t("jobs.launchLead")}</p>
        <ol className="flex flex-col gap-1.5">
          {view.launchable.map((kind, index) => (
            <li key={kind} className="flex items-center gap-3">
              <span className="w-5 text-right text-xs tabular-nums text-white/30">
                {index + 1}
              </span>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => {
                  start(kind);
                }}
                className="min-h-11 flex-1 rounded-lg bg-stage-900 px-3 text-left text-sm hover:bg-stage-800 disabled:opacity-40"
              >
                <span className="font-medium">{stageLabel(kind, t)}</span>
                <span className="ml-2 font-mono text-xs text-white/30">{kind}</span>
              </button>
            </li>
          ))}
        </ol>
        {note && (
          <p role="status" className="mt-2 text-xs text-flash-400">
            {note}
          </p>
        )}
      </div>

      <div>
        <h2 className="mb-2 text-sm font-semibold">{t("jobs.runs")}</h2>
        {view.runs.length === 0 ? (
          <p className="text-sm text-white/50">{t("jobs.noRuns")}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {view.runs.map((run) => (
              <Run
                key={run.id}
                run={run}
                busy={busy === run.id}
                onStop={() => {
                  stop(run.id);
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** One run, with whatever it last said about itself. */
function Run({ run, busy, onStop }: { run: RunView; busy: boolean; onStop: () => void }) {
  const { t } = useTranslation();
  const live = run.status === "pending" || run.status === "running";
  const done = run.progress.done ?? 0;
  const total = run.progress.total ?? null;

  return (
    <li className="rounded-lg bg-stage-900 p-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-sm font-medium">
          {stageLabel(run.kind, t)}
        </span>
        <span
          className={
            run.status === "failed"
              ? "text-xs text-wrong-500"
              : run.status === "done"
                ? "text-xs text-flash-400"
                : "text-xs text-white/50"
          }
        >
          {/* ⚠️ "Stopping" beats "running" and loses to a finished status: a job
              that was cancelled and has stopped says `cancelled`, which is the
              better sentence. */}
          {run.stopping
            ? t("jobs.status.stopping")
            : t(`jobs.status.${run.status}` as never)}
        </span>
      </div>

      {run.progress.phase && (
        <p className="mt-1 text-xs text-white/50">
          {run.progress.phase}
          {" · "}
          <span className="tabular-nums">
            {total === null ? done : `${done} / ${total}`}
          </span>
        </p>
      )}

      {run.result && (
        <p className="mt-1 text-xs tabular-nums text-white/50">
          {t("jobs.result", {
            examined: run.result.examined,
            written: run.result.written,
            skipped: run.result.skipped,
          })}
        </p>
      )}

      {run.last_error && (
        <p className="mt-1 text-xs text-wrong-500">
          {run.last_error}
          {run.attempts > 1 && ` (${String(run.attempts)})`}
        </p>
      )}

      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-xs text-white/30">
          {run.requested_by ?? t("jobs.system")}
        </span>
        {live && !run.stopping && (
          <button
            type="button"
            disabled={busy}
            onClick={onStop}
            className="min-h-9 rounded-lg border border-white/15 px-3 text-xs disabled:opacity-40"
          >
            {t("jobs.stop")}
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * A job kind, as a sentence.
 *
 * # 🔴 Why this is a switch and not `t(\`jobs.stage.${kind}\`)`
 *
 * It **was** that, with `as never` to silence the type error — and the cast is
 * what shipped the bug. The i18n keys are statically typed (`locales/shape.ts`)
 * precisely so a missing one is a compile error; casting to `never` throws that
 * away, and `t()` given an unknown key returns **the key itself**. Production
 * showed an admin the literal string `jobs.stage.spotify_import`.
 *
 * Two of the eight kinds had no label at all: `spotify_import` and
 * `reresolve_year`, neither of which is in `stages::LAUNCHABLE` — so the list of
 * *launchable* stages was complete and the list of things that can **run** was
 * not. `reresolve_year` fires every time a player flags a wrong year (ADR 0009),
 * which is the most common of the lot.
 *
 * # ⚠️ The default is not laziness
 *
 * `run.kind` comes out of `ingest_jobs`, which holds rows queued by **builds
 * that no longer exist** — production still carries `expand_deck`, a kind ADR
 * 0017 removed when decks stopped existing. Those can never be translated,
 * because nothing in the codebase names them. Rendering the kind readably beats
 * rendering a key, and it means a future kind is ugly rather than broken.
 */
function stageLabel(kind: string, t: TFunction): string {
  switch (kind) {
    case "sync_taxonomy":
      return t("jobs.stage.sync_taxonomy");
    case "seed_wikidata":
      return t("jobs.stage.seed_wikidata");
    case "match_canonical":
      return t("jobs.stage.match_canonical");
    case "load_mb_dates":
      return t("jobs.stage.load_mb_dates");
    case "resolve_years":
      return t("jobs.stage.resolve_years");
    case "fetch_clips":
      return t("jobs.stage.fetch_clips");
    case "spotify_import":
      return t("jobs.stage.spotify_import");
    case "reresolve_year":
      return t("jobs.stage.reresolve_year");
    default:
      return kind.replaceAll("_", " ");
  }
}
